/**
 * Turn whatever the scene loaded into flat, editable gaussian columns.
 *
 *  - GSplatData (raw PLY, KHR GLB primitives): used directly.
 *  - GSplatCompressedData (compressed PLY): decompress() (sync).
 *  - GSplatSogData (SOG bundle / meta.json): decompress() (async GPU readback).
 *  - SPZ (playcanvas SpzParser): the quantised streams are decoded on the CPU
 *    with exactly the formulas the engine's SPZ shader uses, so the edit view
 *    matches what was on screen.
 *  - LOD / octree streams (incl. LCC2 imports): see loadLodSource.ts.
 */
import * as pc from 'playcanvas';
import type { SplatColumnSource } from '../core/SplatEditModel';

export class ColumnSource implements SplatColumnSource {
    numSplats: number;
    readonly columns: Map<string, Float32Array>;

    constructor(numSplats: number, columns: Map<string, Float32Array>) {
        this.numSplats = numSplats;
        this.columns = columns;
    }

    getProp(name: string): Float32Array | null {
        return this.columns.get(name) ?? null;
    }
}

interface SpzLike {
    numSplats: number;
    fractionalBits: number;
    shBands: number;
    shDim: number;
    positions: Uint8Array;
    alphas: Uint8Array;
    colors: Uint8Array;
    scales: Uint8Array;
    rotations: Uint8Array;
    sh: Uint8Array | null;
}

export function isSpzData(data: unknown): data is SpzLike {
    const d = data as Partial<SpzLike> | null;
    return !!d && d.positions instanceof Uint8Array && d.rotations instanceof Uint8Array
        && d.alphas instanceof Uint8Array && typeof d.fractionalBits === 'number';
}

const SQRT1_2 = 0.7071067811865476;

/** Decode SPZ attribute streams (as kept by PlayCanvas' SpzGSplatData) to PLY-named columns. */
export function decodeSpzData(d: SpzLike): ColumnSource {
    const n = d.numSplats;
    const cols = new Map<string, Float32Array>();
    const mk = (name: string) => { const a = new Float32Array(n); cols.set(name, a); return a; };
    const x = mk('x'), y = mk('y'), z = mk('z');
    const s0 = mk('scale_0'), s1 = mk('scale_1'), s2 = mk('scale_2');
    const r0 = mk('rot_0'), r1 = mk('rot_1'), r2 = mk('rot_2'), r3 = mk('rot_3');
    const d0 = mk('f_dc_0'), d1 = mk('f_dc_1'), d2 = mk('f_dc_2');
    const op = mk('opacity');

    const posScale = 1 / (1 << d.fractionalBits);
    const pos = d.positions;
    const readPos = (o: number) => {
        let v = pos[o] | (pos[o + 1] << 8) | (pos[o + 2] << 16);
        v = (v << 8) >> 8;
        return v * posScale;
    };
    const q = [0, 0, 0, 0];
    for (let i = 0; i < n; i++) {
        x[i] = readPos(i * 9);
        y[i] = readPos(i * 9 + 3);
        z[i] = readPos(i * 9 + 6);

        s0[i] = d.scales[i * 3] / 16 - 10;
        s1[i] = d.scales[i * 3 + 1] / 16 - 10;
        s2[i] = d.scales[i * 3 + 2] / 16 - 10;

        // colours store the SH DC coefficient as dc * 0.15 + 0.5
        d0[i] = (d.colors[i * 3] / 255 - 0.5) / 0.15;
        d1[i] = (d.colors[i * 3 + 1] / 255 - 0.5) / 0.15;
        d2[i] = (d.colors[i * 3 + 2] / 255 - 0.5) / 0.15;

        const a = d.alphas[i] / 255;
        op[i] = a <= 0 ? -40 : a >= 1 ? 40 : -Math.log(1 / a - 1);

        // smallest-three rotation, (x, y, z, w) order
        const ro = i * 4;
        let comp = (d.rotations[ro] | (d.rotations[ro + 1] << 8) | (d.rotations[ro + 2] << 16) | (d.rotations[ro + 3] << 24)) >>> 0;
        const largest = comp >>> 30;
        let sum = 0;
        for (let k = 3; k >= 0; k--) {
            if (k === largest) continue;
            let v = (comp & 0x1ff) * (SQRT1_2 / 511);
            if (comp & 0x200) v = -v;
            comp >>>= 10;
            q[k] = v;
            sum += v * v;
        }
        q[largest] = Math.sqrt(Math.max(0, 1 - sum));
        r0[i] = q[3]; r1[i] = q[0]; r2[i] = q[1]; r3[i] = q[2];
    }

    if (d.shBands > 0 && d.sh) {
        const per = [0, 3, 8, 15][d.shBands];
        const dim = d.shDim; // coefficients per channel stored in the file (may exceed `per`)
        const rest: Float32Array[] = [];
        for (let k = 0; k < per * 3; k++) rest.push(mk(`f_rest_${k}`));
        const sh = d.sh;
        for (let i = 0; i < n; i++) {
            const base = i * dim * 3;
            for (let k = 0; k < per; k++) {
                for (let c = 0; c < 3; c++) {
                    rest[c * per + k][i] = (sh[base + k * 3 + c] - 128) / 128;
                }
            }
        }
    }
    return new ColumnSource(n, cols);
}

interface GsplatDataLike {
    numSplats: number;
    getProp?: (name: string) => ArrayLike<number> | null | undefined;
    decompress?: () => unknown;
    isCompressed?: boolean;
    isSog?: boolean;
}

/**
 * Decode a loaded gsplat resource. Returns null for LOD/octree resources
 * (use loadLodSource) or anything unrecognised.
 */
export async function decodeGsplatResource(resource: unknown): Promise<SplatColumnSource | null> {
    const res = resource as { octree?: unknown; gsplatData?: GsplatDataLike } | null;
    if (!res || res.octree) return null;
    const data = res.gsplatData;
    if (!data) return null;
    if (isSpzData(data)) return checkDecoded(decodeSpzData(data));
    if (isSogData(data)) return checkDecoded(await decodeSogData(data));
    const needsDecompress = typeof data.decompress === 'function'
        && (data.isCompressed || data.isSog || typeof data.getProp !== 'function');
    if (needsDecompress) {
        const out = await Promise.resolve(data.decompress!());
        return checkDecoded(out as SplatColumnSource);
    }
    if (typeof data.getProp === 'function') return data as SplatColumnSource;
    return null;
}

/**
 * Refuse a decode whose positions came out non-finite: editing (and then
 * saving) that would replace the scene's splat with garbage.
 */
function checkDecoded(src: SplatColumnSource): SplatColumnSource {
    const n = src.numSplats;
    const x = src.getProp('x'), y = src.getProp('y'), z = src.getProp('z');
    if (!x || !y || !z) throw new Error('The splat has no positions.');
    let bad = 0;
    for (let i = 0; i < n; i++) if (!Number.isFinite(x[i]) || !Number.isFinite(y[i]) || !Number.isFinite(z[i])) bad++;
    if (n > 0 && bad > n * 0.5) throw new Error(`The splat could not be decoded (${bad.toLocaleString()} of ${n.toLocaleString()} positions are invalid). Try again in a moment.`);
    return src;
}

interface SogTextureLike {
    width: number;
    height: number;
    device?: { isNull?: boolean };
    read: (x: number, y: number, w: number, h: number, options: object) => Promise<Uint8Array>;
}

interface SogLike {
    isSog: true;
    numSplats: number;
    shBands: number;
    means_l: SogTextureLike; means_u: SogTextureLike; quats: SogTextureLike; scales: SogTextureLike; sh0: SogTextureLike;
    sh_labels?: SogTextureLike; sh_centroids?: SogTextureLike;
    createIter: (p: pc.Vec3, r: pc.Quat, s: pc.Vec3, c: pc.Vec4, sh: Float32Array | null) => { read: (i: number) => void };
    _patchCodebooks?: () => void;
}

function isSogData(d: unknown): d is SogLike {
    const o = d as Partial<SogLike> | null;
    return !!o && o.isSog === true && typeof o.createIter === 'function' && !!o.means_l && typeof o.means_l.read === 'function';
}

const SOG_SH_C0 = 0.28209479177387814;
const SOG_TEXTURES = ['means_l', 'means_u', 'quats', 'scales', 'sh0'] as const;

/**
 * Decode SOG on the CPU into private buffers. The engine's own decompress()
 * parks its GPU readbacks in the shared texture._levels[0] across several
 * awaits, where the viewer's raycaster index build swaps and restores them
 * too, so an edit session opened while that build runs decoded ImageBitmaps
 * as positions (all NaN). Here the readbacks never touch the textures: the
 * engine's iterator runs over a view whose textures carry our copies.
 */
async function decodeSogData(sog: SogLike): Promise<SplatColumnSource> {
    sog._patchCodebooks?.();
    const shBands = sog.shBands;
    const names: string[] = [...SOG_TEXTURES];
    if (shBands > 0) names.push('sh_labels', 'sh_centroids');
    const read = (t: SogTextureLike) => (t.device?.isNull
        ? Promise.resolve(new Uint8Array(t.width * t.height * 4))
        : t.read(0, 0, t.width, t.height, { mipLevel: 0, face: 0, immediate: true }));
    const view = Object.create(sog) as SogLike;
    for (const name of names) {
        const tex = (sog as unknown as Record<string, SogTextureLike>)[name];
        Object.defineProperty(view, name, { value: { width: tex.width, height: tex.height, _levels: [await read(tex)] } });
    }
    Object.defineProperty(view, 'shBands', { value: shBands });

    const n = sog.numSplats;
    const cols = new Map<string, Float32Array>();
    const col = (name: string) => { const a = new Float32Array(n); cols.set(name, a); return a; };
    const x = col('x'), y = col('y'), z = col('z');
    const d0 = col('f_dc_0'), d1 = col('f_dc_1'), d2 = col('f_dc_2'), op = col('opacity');
    const s0 = col('scale_0'), s1 = col('scale_1'), s2 = col('scale_2');
    const r0 = col('rot_0'), r1 = col('rot_1'), r2 = col('rot_2'), r3 = col('rot_3');
    // the iterator writes SH channel-major with a stride of 15 (the 3-band layout);
    // keep only the file's own bands, in their layout
    const per = [0, 3, 8, 15][Math.max(0, Math.min(3, shBands))];
    const rest: Float32Array[] = [];
    for (let k = 0; k < per * 3; k++) rest.push(col(`f_rest_${k}`));
    const p = new pc.Vec3(), r = new pc.Quat(), sc = new pc.Vec3(), c = new pc.Vec4();
    const sh = per ? new Float32Array(45) : null;
    const iter = view.createIter(p, r, sc, c, sh);
    for (let i = 0; i < n; i++) {
        iter.read(i);
        x[i] = p.x; y[i] = p.y; z[i] = p.z;
        r0[i] = r.w; r1[i] = r.x; r2[i] = r.y; r3[i] = r.z;
        s0[i] = sc.x; s1[i] = sc.y; s2[i] = sc.z;
        d0[i] = (c.x - 0.5) / SOG_SH_C0; d1[i] = (c.y - 0.5) / SOG_SH_C0; d2[i] = (c.z - 0.5) / SOG_SH_C0;
        op[i] = c.w <= 0 ? -40 : c.w >= 1 ? 40 : -Math.log(1 / c.w - 1);
        if (sh) for (let j = 0; j < 3; j++) for (let k = 0; k < per; k++) rest[j * per + k][i] = sh[j * 15 + k];
    }
    return new ColumnSource(n, cols);
}

/** Copy rows [offset, offset+count) of `src` into `dst` columns at `at`. */
export function copyRows(
    src: SplatColumnSource, dst: Map<string, Float32Array>, at: number, offset: number, count: number,
): void {
    for (const [name, col] of dst) {
        const s = src.getProp(name);
        if (!s) continue;
        if (s instanceof Float32Array) {
            col.set(s.subarray(offset, offset + count), at);
        } else {
            for (let i = 0; i < count; i++) col[at + i] = s[offset + i];
        }
    }
}
